import { useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import { QUERY_TREEZ_HEADLESS_GROUPS_PAGE } from "@/data";
import { useMeta, useForm, useNotification } from "@/hooks";
import { Content } from "@/layout";
import { Icon } from "@/components";
import {
    Button,
    Heading,
    LoadingProgress,
    Panel,
    Select,
    Table,
    TableFooter,
    TableToolbar,
    Text,
} from "@/ui";
import { NOT_AVAILABLE } from "@/config";
import { stringToSlug } from "@/utils";
import { useStoreContext } from "context";
import styles from "./TreezGroups.module.scss";

const PAGE_SIZE = 25;

const TreezGroups = () => {
    const { user } = useMeta();
    const notify = useNotification();
    const { providers, storeAppsLoading } = useStoreContext();
    const [page, setPage] = useState(1);
    const { inputs, selectChange, setInputs } = useForm();

    const storeOptions = useMemo(
        () =>
            (providers || []).map(provider => ({
                value: String(provider.value),
                label: provider.label,
            })),
        [providers],
    );

    const [getTreezHeadlessGroupsPage, { loading, data, networkStatus }] =
        useLazyQuery(QUERY_TREEZ_HEADLESS_GROUPS_PAGE, {
            skip: user.loading || storeAppsLoading || !inputs.store,
            notifyOnNetworkStatusChange: true,
            fetchPolicy: "network-only",
        });

    useEffect(() => {
        if (!providers?.length || inputs.store) {
            return;
        }

        setInputs(prev => ({
            ...prev,
            store: providers[0]?.value || null,
        }));
    }, [providers, inputs.store, setInputs]);

    useEffect(() => {
        if (storeAppsLoading || !inputs.store || user.loading) {
            return;
        }

        getTreezHeadlessGroupsPage({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                provider_id: inputs.store,
                page,
            },
        });
    }, [
        inputs.store,
        storeAppsLoading,
        page,
        user.loading,
        user.accountId,
        user.storeId,
        getTreezHeadlessGroupsPage,
    ]);

    const pageData = data?.getTreezHeadlessGroupsPage;
    const groups = pageData?.groups ?? [];
    const totalElements = pageData?.total_elements ?? 0;
    const currentPage = page;
    const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE) || 1);
    const rangeFirst =
        totalElements === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeLast = Math.min(currentPage * PAGE_SIZE, totalElements);

    const clipboardCopy = item => {
        const groupUrl = `/product-group/${stringToSlug(item?.name)}?group_id=${
            item?.id
        }`;
        navigator.clipboard.writeText(groupUrl);
        notify(`Group URL: ${groupUrl} is copied`, "success");
    };

    const columns = useMemo(
        () => [
            {
                id: "name",
                header: "Name",
                width: "70%",
                minWidth: 280,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row.name || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "type",
                header: "Type",
                width: "30%",
                minWidth: 200,
                renderCell: row => (
                    <div className={styles.secondaryCell}>
                        {row.type || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "url",
                header: "URL",
                width: 160,
                minWidth: 160,
                renderCell: row => (
                    <span className={styles.copyAction}>
                        <Button
                            iconRight={<Icon color="#727a94" icon="copy" />}
                            onClick={() => clipboardCopy(row)}
                            size="sm"
                            title="Copy Group Url"
                            type="button"
                            variant="tertiary"
                        >
                            Copy URL
                        </Button>
                    </span>
                ),
            },
        ],
        [],
    );

    const tableLoading = loading || storeAppsLoading || networkStatus === 4;

    return (
        <Content>
            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Groups</Heading>
                            <Text tone="secondary" variant="body-md">
                                All Treez Groups available in the system
                            </Text>
                        </div>
                        <Select
                            className={styles.storeSelect}
                            fullWidth
                            label="Store"
                            onValueChange={value => {
                                setPage(1);
                                selectChange({ value }, "store");
                            }}
                            options={storeOptions}
                            value={
                                inputs.store ? String(inputs.store) : undefined
                            }
                        />
                    </div>
                }
                showHeaderDivider
            >
                <TableToolbar
                    count={totalElements}
                    label="Groups"
                    mode="title"
                    searchable={false}
                    updatedAtLabel=""
                />

                {tableLoading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <>
                        <Table
                            columns={columns}
                            emptyMessage="No groups found"
                            getRowId={row => String(row.id)}
                            rows={groups}
                        />
                        <TableFooter
                            leftSlot={
                                <span>
                                    Showing rows {rangeFirst} to {rangeLast} of{" "}
                                    {totalElements}
                                </span>
                            }
                            onPageChange={nextPage => {
                                setPage(
                                    Math.min(totalPages, Math.max(1, nextPage)),
                                );
                            }}
                            page={currentPage}
                            totalPages={totalPages}
                        />
                    </>
                )}
            </Panel>
        </Content>
    );
};

export default TreezGroups;
